/**
 * Propensity Scoring Engine API client.
 *
 * Endpoints are tenant-scoped (`/api/v1/tenants/{tenantId}/propensity/...`).
 * The backend enforces:
 *   - The `propensity_scoring` feature flag must be enabled (else 403).
 *   - Write endpoints require role=admin.
 * The UI also hides admin controls for non-admins as a courtesy.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';

// ---------------------------------------------------------------------------
// Type contracts (mirror backend dataclasses + Pydantic models)
// ---------------------------------------------------------------------------

export interface UseCaseRead {
  key: string;
  name: string;
  description: string;
  target_outcome: string;
  industry_hint: string | null;
}

export type ConfigStatus = 'draft' | 'validated' | 'approved' | 'archived';
export type ValidationStatus = 'refuse' | 'warn' | 'green';

export type MappingStatus = 'pending' | 'approved';
export type SyncStatus = 'pending' | 'running' | 'success' | 'failed';

export interface ConfigSummary {
  config_id: string;
  use_case_key: string;
  display_name: string;
  status: ConfigStatus;
  validation_status: ValidationStatus | null;
  selected_model_key: string | null;
  approved_at: string | null;
  updated_at: string;
  /** null on legacy configs that pre-date the mapping workflow. */
  mapping_status: MappingStatus | null;
  last_mapping_sync_status: SyncStatus | null;
}

export interface SegmentRuleRead {
  name: string;
  condition: string;
  recommended_action: string;
}

export interface FeatureSpecRead {
  name: string;
  kind: string;
  inputs: string[];
  description: string | null;
  label: string | null;
}

export interface DataQualityRead {
  min_rows: number;
  min_positive_rate: number;
  max_positive_rate: number;
  refuse_below_auc: number;
  warn_below_auc: number;
  green_above_auc: number;
}

export interface PropensityConfigPayload {
  use_case: {
    key: string;
    name: string;
    description: string;
    target_outcome: string;
    industry_hint: string | null;
  };
  /** Physical target column in the Redshift mart. */
  target_field: string;
  /** Values in the target column that count as "event happened" when
   * target_positive_when is 'values'. */
  target_positive_value: string[] | null;
  /** How to binarise the target column: values | present | absent. */
  target_positive_when: 'values' | 'present' | 'absent';
  /** Restrict scoring to rows where the target column is present/absent. */
  score_subset_when: 'present' | 'absent' | null;
  features: FeatureSpecRead[];
  segments: SegmentRuleRead[];
  data_quality: DataQualityRead;
  enabled_models: string[] | null;
  objective: string;
}

export interface ValidationMetrics {
  auc_mean?: number;
  auc_ci_low?: number;
  auc_ci_high?: number;
  precision_at_top_decile_mean?: number;
  precision_at_top_decile_ci_low?: number;
  precision_at_top_decile_ci_high?: number;
  lift_at_top_decile_mean?: number;
  brier_score?: number;
  n_train?: number;
  n_positive?: number;
  positive_rate?: number;
  candidates_ranked?: Array<{ key: string; score: number; reason?: string }>;
  per_model?: Record<string, {
    auc_mean: number;
    auc_ci_low: number;
    auc_ci_high: number;
    precision_at_top_decile_mean: number;
    precision_at_top_decile_ci_low: number;
    precision_at_top_decile_ci_high: number;
    lift_at_top_decile_mean: number;
    brier_score: number;
  }>;
  /** Narrative reason from the runner — refuse cause, warn rationale,
   * or selector explanation. Always present; empty list when missing. */
  messages?: string[];
  /** One-line champion-selection rationale (e.g. "Highest precision@10 …"). */
  selection_reason?: string | null;
}

export interface SnapshotStatsRead {
  n_rows: number;
  n_positive: number;
  positive_rate: number;
  max_event_date: string | null;
  schema_fingerprint: string;
}

export interface ConfigDetail extends ConfigSummary {
  payload: PropensityConfigPayload;
  validation_metrics: ValidationMetrics | null;
  validation_snapshot: SnapshotStatsRead | null;
  warn_acknowledged: boolean;
  field_mapping: FieldMappingRead | null;
  last_mapping_sync_at: string | null;
  /** Ops-controlled sync routing; set manually in DB (e.g. mart_loader). */
  config_metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Field-mapping types (mirror backend Pydantic models)
// ---------------------------------------------------------------------------
//
// `stage` carries the discovery provenance:
//   - exact         : Stage 1 free win (case-insensitive name match)
//   - embedding     : Stage 2 fallback (LLM call failed/skipped)
//   - llm_rerank    : Stage 3 selected this from the embedding short-list
//   - manual        : the admin edited the mapping by hand
//   - no_match      : nothing in the ontology matched
//
// `multi_source_strategy` describes how the loader will combine
// primary + additional sources at sync time:
//   - single   : just the primary source
//   - union    : append rows from each source (vertical combine)
//   - coalesce : take the first non-null source per row (horizontal combine)

export type MappingStage = 'exact' | 'embedding' | 'llm_rerank' | 'manual' | 'no_match';
export type MultiSourceStrategy = 'single' | 'union' | 'coalesce';

export interface MappingSourceRead {
  canonical_entity_id: string;
  canonical_entity_name: string;
  canonical_field_name: string;
  connector_id: string;
  source_entity: string;
  source_field: string;
  confidence: number;
  reasoning: string;
}

export interface MartColumnMappingRead {
  mart_column: string;
  stage: MappingStage;
  auto_suggested: boolean;
  reviewed_by_user: boolean;
  multi_source_strategy: MultiSourceStrategy;
  primary_source: MappingSourceRead | null;
  additional_sources: MappingSourceRead[];
}

export interface FieldMappingRead {
  version: number;
  discovered_at: string;
  ontology_version: string;
  mappings: MartColumnMappingRead[];
  counts: Record<string, number>;
}

export type DiscoveryStatus = 'running' | 'done' | 'failed';

/** Streaming-progress envelope for an in-flight discovery run.
 *
 * The panel uses `all_columns` to render the skeleton table on first
 * paint (without waiting for any LLM call to return) and `partial_mappings`
 * to flip individual rows from skeleton → real as the worker streams
 * results in.
 *
 * Lifecycle:
 *  - POST /mapping/discover initialises this (status='running',
 *    `all_columns` populated, `partial_mappings` empty).
 *  - As Stage 1 / Stage 3 produce per-column mappings the worker
 *    appends them into `partial_mappings`. The polling fetch picks
 *    them up.
 *  - On success: backend clears `discovery_progress` and writes
 *    `field_mapping` in the same transaction. The next poll sees
 *    a populated `field_mapping` and stops polling.
 *  - On failure: status='failed', `error` carries the message; UI
 *    surfaces a retry CTA.
 */
export interface DiscoveryProgress {
  status: DiscoveryStatus;
  started_at: string;
  completed_at: string | null;
  /** Mappable mart columns (excludes role='metadata'). The panel
   * renders one row per entry, showing the row as a skeleton until
   * its name appears as a key in `partial_mappings`. */
  all_columns: string[];
  partial_mappings: Record<string, MartColumnMappingRead>;
  error: string | null;
}

export interface FieldMappingResponse {
  mapping_status: MappingStatus | null;
  field_mapping: FieldMappingRead | null;
  /** Latest GlobalCanonicalSchema version for the tenant. The UI shows
   * a "re-run discovery" banner when this differs from
   * `field_mapping.ontology_version`. */
  latest_ontology_version: string | null;
  sync_status: SyncStatus | null;
  sync_completed_at: string | null;
  /** Present (with status='running') while a discovery worker is
   * streaming partial results; null otherwise. */
  discovery_progress: DiscoveryProgress | null;
}

// ---------------------------------------------------------------------------
// Mart preview — surfaces the per-tenant Redshift mart in the detail UI
// ---------------------------------------------------------------------------
//
// Three-state UX:
//   - health.exists === false && !health.error → mart not provisioned
//     yet. We render a "table will be created when the feature was
//     enabled" hint (the provision hook should have done this; this
//     is the corner case where it failed).
//   - health.exists === false && health.error  → couldn't reach the
//     warehouse. Render the error verbatim so admins can pass it to
//     us if they need help.
//   - health.exists === true                   → render the columns +
//     fill rates + sample rows.

export type MartColumnRole = 'identifier' | 'target' | 'feature' | 'metadata';

export interface MartColumnPreview {
  name: string;
  sql_type: string;
  nullable: boolean;
  role: MartColumnRole;
  semantic_type: string | null;
  description: string | null;
}

export interface MartSchemaPreview {
  table_name: string;
  description: string;
  /** Fully-resolved Redshift schema name (`propensity_<tenant_uuid>`). */
  schema_name: string;
  columns: MartColumnPreview[];
}

export interface MartHealth {
  exists: boolean;
  row_count: number | null;
  has_data: boolean;
  /** Surfaced when the warehouse is unreachable (vs. simply unprovisioned). */
  error: string | null;
}

export interface MartFillRate {
  non_null: number;
  total: number;
}

export interface MartPreview {
  schema: MartSchemaPreview;
  health: MartHealth;
  fill_rates: Record<string, MartFillRate>;
  sample_rows: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Target value picker
// ---------------------------------------------------------------------------
//
// Distinct values + counts for the use case's target column. Drives the
// "Target value" multi-select on the model detail page so the admin can
// pick what counts as "event happened" without typing.

export interface TargetValueOption {
  /** Distinct value, str-coerced; null when the row was DB NULL. */
  value: string | null;
  count: number;
}

export interface TargetValues {
  column: string;
  mode?: 'values' | 'presence';
  values: TargetValueOption[];
  /** True when more distinct values exist than the picker can show.
   * The UI surfaces a "more values exist" hint when this is true. */
  truncated: boolean;
  limit: number;
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

const authHeaders = (): Record<string, string> => {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function readJson<T>(resp: Response): Promise<T> {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const detail = (data && (data.detail || data.message)) || `Request failed (${resp.status})`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return data as T;
}

const tenantPath = (tenantId: string) => `${tenantApiPrefix(tenantId)}/propensity`;

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listUseCases(tenantId: string): Promise<UseCaseRead[]> {
  const resp = await fetch(buildApiUrl(`${tenantPath(tenantId)}/use-cases`), {
    headers: authHeaders(),
    cache: 'no-store',
  } as RequestInit);
  return readJson<UseCaseRead[]>(resp);
}

export async function listConfigs(
  tenantId: string,
  options: { includeArchived?: boolean } = {},
): Promise<ConfigSummary[]> {
  const qs = options.includeArchived ? '?include_archived=true' : '';
  const resp = await fetch(buildApiUrl(`${tenantPath(tenantId)}/configs${qs}`), {
    headers: authHeaders(),
    cache: 'no-store',
  } as RequestInit);
  return readJson<ConfigSummary[]>(resp);
}

export async function getConfig(
  tenantId: string, configId: string,
): Promise<ConfigDetail> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}`),
    { headers: authHeaders(), cache: 'no-store' } as RequestInit,
  );
  return readJson<ConfigDetail>(resp);
}

export async function createConfig(
  tenantId: string, body: { use_case_key: string; display_name?: string },
): Promise<ConfigDetail> {
  const resp = await fetch(buildApiUrl(`${tenantPath(tenantId)}/configs`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  } as RequestInit);
  return readJson<ConfigDetail>(resp);
}

export interface UpdateConfigPatch {
  display_name?: string;
  segments?: SegmentRuleRead[];
  enabled_models?: string[];
  /** Tenant-specific override when target_positive_when='values'. */
  target_positive_value?: string[];
  /** Override how the target column is binarised. */
  target_positive_when?: 'values' | 'present' | 'absent';
}

export async function updateConfig(
  tenantId: string, configId: string, patch: UpdateConfigPatch,
): Promise<ConfigDetail> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(patch),
    } as RequestInit,
  );
  return readJson<ConfigDetail>(resp);
}

export async function validateConfig(
  tenantId: string, configId: string,
  options: { rowLimit?: number } = {},
): Promise<ConfigDetail> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/validate`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ row_limit: options.rowLimit ?? null }),
    } as RequestInit,
  );
  return readJson<ConfigDetail>(resp);
}

export async function approveConfig(
  tenantId: string, configId: string, acknowledgeWarn: boolean,
): Promise<ConfigDetail> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/approve`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ acknowledge_warn: acknowledgeWarn }),
    } as RequestInit,
  );
  return readJson<ConfigDetail>(resp);
}

export async function archiveConfig(
  tenantId: string, configId: string,
): Promise<ConfigDetail> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/archive`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    } as RequestInit,
  );
  return readJson<ConfigDetail>(resp);
}

export async function getMartPreview(
  tenantId: string, configId: string, options: { sampleLimit?: number } = {},
): Promise<MartPreview> {
  const qs = options.sampleLimit ? `?sample_limit=${options.sampleLimit}` : '';
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mart-preview${qs}`),
    { headers: authHeaders(), cache: 'no-store' } as RequestInit,
  );
  return readJson<MartPreview>(resp);
}

export async function getTargetValues(
  tenantId: string, configId: string, options: { limit?: number } = {},
): Promise<TargetValues> {
  const qs = options.limit ? `?limit=${options.limit}` : '';
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/target-values${qs}`),
    { headers: authHeaders(), cache: 'no-store' } as RequestInit,
  );
  return readJson<TargetValues>(resp);
}

// ---------------------------------------------------------------------------
// Field-mapping API
// ---------------------------------------------------------------------------

/** Fetch the current mapping state — saved mapping, in-flight
 * discovery progress, sync status. Pure read; no side effects. The
 * UI also uses this as its polling endpoint while a discovery is
 * running (every 750ms until `discovery_progress.status !== 'running'`). */
export async function getFieldMapping(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping`),
    { headers: authHeaders(), cache: 'no-store' } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Kick off a discovery run in the background. Returns 202 with the
 * initial `discovery_progress` envelope (status='running', `all_columns`
 * populated, no rows yet). Idempotent — if a run is already in flight
 * or the config already has a mapping, the backend no-ops and returns
 * current state. The caller starts polling getFieldMapping after
 * this succeeds. */
export async function startMappingDiscovery(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping/discover`),
    {
      method: 'POST',
      headers: authHeaders(),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Force a re-run of the 3-stage discovery pipeline (async). Clears
 * the saved mapping, resets `mapping_status` to 'pending', and
 * schedules a fresh discovery run with `force_reindex=true` (rebuilds
 * field cards). UI confirms before calling — re-running discards
 * any in-progress edits. Returns 202 with the new `discovery_progress`. */
export async function rerunMappingDiscovery(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping/suggest`),
    {
      method: 'POST',
      headers: authHeaders(),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Persist the admin's mapping edits. Send the entire mapping list
 * (vs. a per-column patch) — last write wins, the UI re-reads after. */
export async function updateFieldMapping(
  tenantId: string, configId: string, mappings: MartColumnMappingRead[],
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ mappings }),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Freeze the mapping. Required before sync can be started. The
 * backend rejects approval when any mappable column has no primary
 * source, surfacing a 400 with the offending columns listed. */
export async function approveFieldMapping(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping/approve`),
    {
      method: 'POST',
      headers: authHeaders(),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Reopen an approved mapping for editing. Flips `mapping_status`
 * back to 'pending' so Re-run / Save / Approve become available
 * again. The saved field_mapping blob is preserved (no work lost),
 * and the existing sync state is left alone (the synced data is
 * still in the mart — only the editability of the mapping changes).
 * Backend returns 400 if the mapping isn't currently approved. */
export async function unfreezeFieldMapping(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/mapping/unfreeze`),
    {
      method: 'POST',
      headers: authHeaders(),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}

/** Kick off the (currently stubbed) sync. The backend immediately
 * flips `last_mapping_sync_status` to RUNNING and a background task
 * flips it to SUCCESS after ~2s; the UI polls for the transition. */
export async function startMappingSync(
  tenantId: string, configId: string,
): Promise<FieldMappingResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantPath(tenantId)}/configs/${configId}/sync`),
    {
      method: 'POST',
      headers: authHeaders(),
    } as RequestInit,
  );
  return readJson<FieldMappingResponse>(resp);
}
